import { useRef, useState } from 'react';
import Image from 'next/image';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { Grid } from '@splidejs/splide-extension-grid';

import { ConditionalWrapper } from '@/hoc';
import { ArrowLeft, ArrowRight } from '@/icons';
import { NextLink } from '@/atoms';
import { IconButton } from '@/molecules';

import { removeBaseURL } from '@/lib/utils';

import type { Splide as SplideType } from '@splidejs/splide';
import type { ElementType, RefObject } from 'react';
import type { TypeMosaicProps } from './types';

const MosaicCard = ({ card }: { card: TypeMosaicProps }) => {
  const Comp: ElementType = Boolean(card.link?.url) ? 'a' : 'div';

  return (
    <ConditionalWrapper
      condition={Boolean(card.link?.url)}
      wrapper={(c) => (
        <NextLink href={removeBaseURL(card.link?.url ?? '/')} passHref>
          {c}
        </NextLink>
      )}
    >
      <Comp
        className="u-aspect-square relative flex items-center justify-center"
        {...(card.typeBackground === 'color' && {
          style: {
            backgroundColor: card.color,
          },
        })}
      >
        <p
          className="u-headline u-headline--h2 absolute z-30"
          style={{
            color: card.titleColor,
          }}
        >
          {card.title}
        </p>

        {card.typeBackground === 'image' && (
          <>
            {card.overlay && card.overlay > 0 && (
              <div
                style={{
                  opacity: card.overlay / 100,
                }}
                className="absolute inset-0 z-20 bg-black"
              />
            )}

            <Image
              src={card.image.sourceUrl}
              alt={card.image.altText}
              fill
              className="h-full object-cover object-center"
            />
          </>
        )}
      </Comp>
    </ConditionalWrapper>
  );
};

const MosaicController = ({ splideRef }: { splideRef: RefObject<Splide> }) => {
  const [controllerState, setControllerState] = useState({
    prevController: true,
    nextController: false,
  });

  const updateControllers = (splide: SplideType) => {
    setControllerState({
      nextController:
        splide?.Components?.Controller.getIndex() ===
        splide?.Components?.Controller.getEnd(),
      prevController: splide?.Components?.Controller.getIndex() === 0,
    });
  };

  return (
    <div className="mt-2 flex max-w-[1160px] flex-row items-center justify-end gap-2 px-2 max-sm:hidden">
      <IconButton
        onClick={() => {
          if (splideRef.current?.splide) {
            const newSplide = splideRef.current.splide.go('<');
            updateControllers(newSplide);
          }
        }}
        disabled={controllerState.prevController}
        className="shrink-0"
        style="dashed-outline"
        size="medium"
        aria-label="Slide Anterior"
      >
        <ArrowLeft />
      </IconButton>

      <IconButton
        onClick={() => {
          if (splideRef.current?.splide) {
            const newSplide = splideRef.current.splide.go('>');
            updateControllers(newSplide);
          }
        }}
        disabled={controllerState.nextController}
        className="shrink-0"
        style="dashed-outline"
        size="medium"
        aria-label="Slide Siguiente"
      >
        <ArrowRight />
      </IconButton>
    </div>
  );
};

const Mosaic = ({ cards }: { cards: TypeMosaicProps[] }) => {
  const splideRef = useRef<Splide>(null);

  return (
    <>
      {/**
       * TODO: La versión que tenemos de Splide no tiene el prop Children ya que en React 17 se añadia automaticamente pero en la 18 no
       * Cuando actualicemos Splide a la última versión, podemos quitar el @ts-ignore
       * @eslint-disable-next-line @typescript-eslint/ban-ts-comment
       * @ts-expect-error types are not for react 18 */}
      <Splide
        ref={splideRef}
        Extensions={{ Grid }}
        options={{
          perPage: 4,
          pagination: false,
          drag: cards.length > 4,
          arrows: false,
          grid: {
            rows: 1,
          },
          width: 1160,
          breakpoints: {
            640: {
              perPage: 2,
              drag: false,
              grid: {
                rows: cards.length / 2,
              },
            },
          },
        }}
      >
        {cards.map((card, i) => (
          <SplideSlide key={i}>
            <MosaicCard {...{ card }} />
          </SplideSlide>
        ))}
      </Splide>
      {cards.length > 4 && <MosaicController splideRef={splideRef} />}
    </>
  );
};

export { Mosaic };
